import * as React from "react";
import { Checkbox } from "antd";
import { Cell2Number } from "src/components/Sheet2/components";
import { Sheet2Store } from "src/components/Sheet2/Sheet2Store";
import { classNames } from "src/common/classNames";
import { ZUid } from "src/types/zUid";
import styles from "./Sample2Table.module.less";
import { ZSample2Row } from "../../ZSample2";
import { Sample2Column } from "./Sample2Column";
import { canUpdateRow } from "../sample2utils";
import { UpdateColumnButton } from "../UpdateColumnButton";

export const buildSample2Columns = (params: {
  sheetStore: Sheet2Store;
  onChange: (
    newRow: ZSample2Row,
    field: "actualValue" | "newValue",
  ) => Promise<() => void>;
  selected: Set<ZUid | "allPossible" | "empty">;
  onChangeSelect: (id: ZUid | "allPossible" | "empty") => void;
  isReadOnly: boolean;
  rowsSrc: { rows: ZSample2Row[] };
}): Sample2Column[] => {
  const {
    sheetStore,
    onChange,
    selected,
    onChangeSelect,
    isReadOnly,
    rowsSrc,
  } = params;
  const changeNum = async (
    value: unknown,
    row: ZSample2Row,
    field: "actualValue" | "newValue",
  ) => {
    const numValue: number | null = typeof value === "number" ? value : null;
    const currentRow = rowsSrc.rows.find(({ id }) => id === row.id);
    if (!currentRow) throw Error("Не найдены данные");
    const newRow: ZSample2Row = {
      ...currentRow,
      [field]: numValue,
    };
    return onChange(newRow, field);
  };
  const columns: Sample2Column[] = [
    {
      key: "point",
      title: "Point",
      minWidth: 100,
      width: "1fr",
      render: ({ point }) => <Static>{point}</Static>,
    },
    {
      key: "description",
      title: "Description",
      minWidth: 200,
      width: "9fr",
      render: ({ description }) => <Static>{description}</Static>,
    },
    {
      key: "QC",
      title: "QC",
      minWidth: 48,
      align: "center",
      render: ({ criticality }) => (
        <Static centred>
          <Checkbox checked={criticality} disabled />
        </Static>
      ),
    },
    {
      key: "tolMinus",
      title: "Tol -",
      minWidth: 70,
      width: "1fr",
      render: ({ tolMinus }) => <Static>{num(tolMinus)}</Static>,
    },
    {
      key: "tolPlus",
      title: "Tol +",
      minWidth: 70,
      width: "1fr",
      render: ({ tolPlus }) => <Static>{num(tolPlus)}</Static>,
    },
    {
      key: "requestedValue",
      title: "Requested",
      minWidth: 150,
      width: "1fr",
      render: ({ requestedValue }) => <Static>{num(requestedValue)}</Static>,
    },
    {
      key: "Difference",
      title: "Difference",
      minWidth: 100,
      width: "1fr",
      render: (row) => (
        <Static>
          <span>{calcDiff(row, sheetStore)}</span>
        </Static>
      ),
    },
    {
      key: "actualValue",
      title: "Actual",
      minWidth: 150,
      width: "1fr",
      render: (row, i) => {
        const cls = classNames([
          [isRedActual(row, sheetStore), styles.actualRed],
        ]);
        return isReadOnly ? (
          <Static className={cls}>{numNullish(row.actualValue)}</Static>
        ) : (
          <Cell2Number
            cell={{
              cellKey: mkCellKey("actualValue", row.id),
              pos: { x: 0, y: i },
              store: sheetStore,
              value: row.actualValue,
              emptyValue: null,
              save: (value: unknown) => changeNum(value, row, "actualValue"),
            }}
            editorProps={{
              className: cls,
              min: 0,
            }}
          />
        );
      },
    },
    {
      key: "newValue",
      title: "New",
      minWidth: 150,
      width: "1fr",
      render: (row, i) =>
        isReadOnly ? (
          <Static>{numNullish(row.newValue)}</Static>
        ) : (
          <Cell2Number
            cell={{
              cellKey: mkCellKey("newValue", row.id),
              pos: { x: 1, y: i },
              store: sheetStore,
              value: row.newValue,
              emptyValue: null,
              save: (value: unknown) => changeNum(value, row, "newValue"),
            }}
            editorProps={{ min: 0 }}
          />
        ),
    },
    {
      key: "Update",
      title: <UpdateColumnButton />,
      minWidth: 88,
      width: "1fr",
      align: "center",
      render: (row) => (
        <Static centred>
          <Checkbox
            checked={selected.has(row.id)}
            disabled={!canUpdateRow(row) || isReadOnly}
            onChange={() => onChangeSelect(row.id)}
          />
        </Static>
      ),
    },
  ];
  return columns;
};

const mkCellKey = (field: keyof ZSample2Row, id: ZUid) => `${field}:${id}`;

const num = (value: number): string => String(value);

const numNullish = (value: number | null | undefined) =>
  typeof value === "number" ? num(value) : "";

const getActual = (
  row: ZSample2Row,
  store: Sheet2Store,
): number | null | undefined => {
  const a = store.cellCurValues[mkCellKey("actualValue", row.id)];
  return typeof a === "number" ? a : row.actualValue;
};

/**
 * Авторасчёт по формуле = Actual - Requested
 * Обновляется inline в момент редактирования поля "Actual"
 */
const calcDiff = (row: ZSample2Row, store: Sheet2Store): string => {
  const a = getActual(row, store);
  if (typeof a !== "number") return "";
  const d = a - row.requestedValue;
  return adaptiveRound(d, [row.requestedValue, a]);
};

// В JavaScript тип Number (64-битный IEEE 754) обеспечивает точное представление чисел с плавающей запятой
// до 15–17 значащих цифр. Количество знаков после запятой не ограничено жестко, но точность теряется при превышении
// этого общего предела значащих цифр из-за перехода к двоичной системе
const precLimit = 15;

// Правила округления, родившиеся в головах Васи и Влада 😱
// http://jira.d4r.int/browse/LPLM-4998
const adaptiveRound = (value: number, depends: number[]): string => {
  const precision = depends.reduce((acc: number | null, val: number) => {
    const p = getPrecision(val);
    if (acc === null || p === null) return null;
    return Math.max(acc, p);
  }, 0);
  if (precision === null || precision > precLimit) {
    return String(value); // Невозможно применить правила.
  }
  const fixed = value.toFixed(precision);
  return (+fixed).toString(); // чтобы избавиться от лишних нулей в конце
};

const getPrecision = (value: number): number | null => {
  // Не имеет смысла использование математических операций типа выделения дробной части.
  // Потому что с высокой вероятностью появятся ошибки переполнения разрядной сетки.
  const src = String(value);
  // Если у числа нет дробной части, то 0
  if (!src.includes(".")) return 0;
  const res = /\.(\d+)$/.exec(src);
  if (!res) return null; // Нет возможности выделить дробную часть. Например, если число в экспоненциальной форме.
  return res[1]?.length ?? null;
};

const isRedActual = (row: ZSample2Row, store: Sheet2Store): boolean => {
  const actual = getActual(row, store);
  if (typeof actual !== "number") return false;

  //  Requested + Tol+ < Actual ИЛИ Requested - Tol- > Actual
  const { requestedValue: requested, tolMinus, tolPlus } = row;
  return requested + tolPlus < actual || requested - tolMinus > actual;
};

type PropsStatic = {
  centred?: boolean;
  children: React.ReactNode;
  className?: string;
};

const Static: React.FC<PropsStatic> = ({ children, centred, className }) => (
  <div
    className={classNames([
      styles.static,
      [centred, styles.centred],
      className,
    ])}
  >
    {children}
  </div>
);
